import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import { displayValue, toLabel } from "../../utils/bookingDetailParsers";

function formatVal(val) {
  if (val === null || val === undefined || val === "") return "-";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

function JsonViewerModal({ title, raw, onClose }) {
  if (!raw) return null;
  const formatted = typeof raw === "string" ? raw : JSON.stringify(raw, null, 2);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold hover:bg-gray-100">
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto flex-1 min-h-0">
          <pre className="text-sm text-gray-800 bg-gray-50 p-4 rounded-xl border overflow-x-auto whitespace-pre-wrap font-mono">{formatted}</pre>
        </div>
      </div>
    </div>
  );
}

// flight_booking_attempts.booking_payload is the client's create_booking
// request body stored flat (same shape newBooking() reads req.body as).
// Confirmed against real stored rows: the CONFIRMED flight/fare data lives
// under `flight_segments` (array of trip-groups, each an array of segment
// objects shaped like createBookingConverter's formatSegment() output —
// segment.airline.airline_code/flight_number, segment.origin/destination
// with airport_code + dep_time/arr_time), NOT the top-level `segments` key
// (that's the flat search criteria: origin/destination/d_time/a_time per
// leg, used only to originally search). fare_details holds base_fare/tax/
// total/payable plus a ready-made fare_info=[{label,value}] array and the
// promo/wallet/gift-card flags (nested there, not top-level).
function flatSegmentsFromPayload(p) {
  const groups = Array.isArray(p?.flight_segments) ? p.flight_segments : [];
  return groups.flat().filter(Boolean);
}

const isMultiCityAttempt = (a) => Number(a?.booking_payload?.journey_type) === 3;
const attemptTripNumber = (a) => Number(a?.trip_indicator_id ?? a?.booking_payload?.trip_indicator_id) || null;

// Multi city attempts of one trace, ordered by trip and labelled "Trip N". A
// retried trip has more than one attempt, so a repeat gets a running count
// ("Trip 2 (2)") to keep every tab distinct.
function multiCityAttemptTrips(list) {
  const sorted = [...list].sort(
    (a, b) => (attemptTripNumber(a) ?? 999) - (attemptTripNumber(b) ?? 999) || new Date(a.created_at) - new Date(b.created_at)
  );
  const seen = {};
  return sorted.map((a, idx) => {
    const n = attemptTripNumber(a) ?? idx + 1;
    seen[n] = (seen[n] ?? 0) + 1;
    return { attempt: a, label: seen[n] > 1 ? `Trip ${n} (${seen[n]})` : `Trip ${n}` };
  });
}

function payloadFields(attempt) {
  const p = attempt?.booking_payload || {};
  const leadPax = Array.isArray(p.passengers) ? p.passengers.find((x) => x?.is_lead) || p.passengers[0] : null;
  const flatSegs = flatSegmentsFromPayload(p);
  const firstSeg = flatSegs[0];
  // A combined round trip (journey_type 2, onward + return trip-groups in one
  // payload) ends back at the origin, so its destination is the end of the
  // onward trip-group. One way / multi city / separate legs keep the last segment.
  const tripGroups = Array.isArray(p.flight_segments) ? p.flight_segments.filter((g) => Array.isArray(g) && g.length) : [];
  const isCombinedRoundTrip = Number(p.journey_type) === 2 && tripGroups.length > 1;
  const lastSeg = isCombinedRoundTrip ? tripGroups[0][tripGroups[0].length - 1] : flatSegs[flatSegs.length - 1];
  const searchLeg = Array.isArray(p.segments) ? p.segments[0] : null;
  const fd = p.fare_details || {};

  const airlineCodes = [...new Set(flatSegs.map((s) => s?.airline?.airline_code).filter(Boolean))];
  const airlineCrafts = [...new Set(flatSegs.map((s) => s?.airline_craft).filter(Boolean))];

  return [
    { label: "Booking ID", value: attempt.linked_booking_id ?? "Not created yet", hint: attempt.is_pending_booking ? "pending" : null },
    { label: "Booking Reference Number", value: attempt.linked_booking_reference ?? "Not created yet" },
    { label: "User ID", value: attempt.user_id ?? p.user_id },
    { label: "Trace ID", value: attempt.trace_id },
    {
      label: "Username",
      value: leadPax ? `${leadPax.title ?? ""} ${leadPax.first_name ?? ""} ${leadPax.last_name ?? ""}`.trim() : null,
    },
    { label: "Payment ID", value: attempt.payment_id ?? p.payment_id },
    { label: "Fare Type", value: displayValue("fare_type", attempt.fare_type ?? p.fare_type ?? (p.is_low_cost ? "low_cost" : "regular")) },
    { label: "Last Ticket Date", value: null },
    { label: "Ticket Advisary", value: null },
    { label: "Airline Code", value: airlineCodes.join(", ") || null },
    { label: "Airline Craft", value: airlineCrafts.join(", ") || null },
    { label: "PNR", value: p.pnr, hint: "empty until created" },
    { label: "Airline PNR", value: null, hint: "assigned once the airline confirms" },
    { label: "Origin", value: firstSeg?.origin?.airport_code ?? firstSeg?.origin?.city_code ?? searchLeg?.origin },
    {
      label: "Destination",
      value: lastSeg?.destination?.airport_code ?? lastSeg?.destination?.city_code ?? searchLeg?.destination,
      hint: isCombinedRoundTrip ? "round trip" : null,
    },
    {
      label: "Trip Indicator ID",
      value:
        isMultiCityAttempt(attempt) && attemptTripNumber(attempt)
          ? `Trip ${attemptTripNumber(attempt)}`
          : displayValue("trip_indicator_id", attempt.trip_indicator_id),
    },
    { label: "Journey Type", value: displayValue("journey_type", p.journey_type) },
    { label: "Promo Code", value: p.promo_code ?? fd.promo_code },
    { label: "Total Fare", value: fd.total ?? fd.payable ?? p.grand_total },
    { label: "Passenger Count", value: Array.isArray(p.passengers) ? p.passengers.length : null },
    { label: "Cancellation Type", value: null },
    { label: "Cancellation Response", value: null },
    { label: "Attempt Status", value: attempt.status },
    { label: "Attempt Message", value: attempt.message },
    { label: "Attempt Created (Created At)", value: attempt.created_at ? new Date(attempt.created_at).toLocaleString() : null },
  ];
}

function formatINR(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
}

// Seat / meal / baggage entries per passenger, shown with their raw code
// and description as sent in the booking payload.
function PassengerSsrTable({ person }) {
  const rows = [
    ["Seat", person.seat],
    ["Meal", person.meal],
    ["Baggage", person.baggage],
  ].flatMap(([kind, list]) =>
    (Array.isArray(list) ? list : []).map((item) => ({
      kind,
      route: item?.origin && item?.destination ? `${item.origin} → ${item.destination}` : "-",
      flight: item?.airline_code && item?.flight_number ? `${item.airline_code} ${item.flight_number}` : "-",
      code: item?.code || "-",
      description: item?.description ?? "-",
      price: item?.price,
    }))
  );

  if (rows.length === 0) return null;

  return (
    <div className="border-t">
      <div className="bg-gray-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-600">Seat / Meal / Baggage</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-2 px-4 font-semibold">Type</th>
            <th className="py-2 px-4 font-semibold">Route</th>
            <th className="py-2 px-4 font-semibold">Flight</th>
            <th className="py-2 px-4 font-semibold">Code</th>
            <th className="py-2 px-4 font-semibold">Description</th>
            <th className="py-2 px-4 font-semibold text-right">Price</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="py-2 px-4 text-gray-600">{r.kind}</td>
              <td className="py-2 px-4 text-gray-800">{r.route}</td>
              <td className="py-2 px-4 text-gray-800">{r.flight}</td>
              <td className="py-2 px-4 text-gray-800">{r.code}</td>
              <td className="py-2 px-4 text-gray-800">{String(r.description)}</td>
              <td className="py-2 px-4 text-right tabular-nums text-gray-800">{formatINR(r.price || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// "Booking Details" tab content — same four tabs the real Bookings detail
// page exposes, sourced from the equivalent piece of booking_payload since
// there's no real bookings row yet: search criteria -> User Booking Data,
// passengers -> Booking Passenger Details, fare_info -> Fare Info Summary,
// full fare_details -> Search Fare Detail.
function BookingDetailsTabsModal({ type, payload, onClose }) {
  if (!type) return null;
  const p = payload || {};

  let title = "";
  let body = null;

  if (type === "search") {
    title = "User Booking Data (Search Criteria)";
    const legs = Array.isArray(p.segments) ? p.segments : [];
    body = (
      <div className="space-y-3">
        {legs.length === 0 ? (
          <div className="text-sm text-gray-600">No search criteria stored.</div>
        ) : (
          legs.map((leg, idx) => (
            <div key={idx} className="rounded-xl border overflow-hidden">
              <div className="bg-sky-100 px-4 py-2 text-sm font-bold text-gray-800">Leg {idx + 1}</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(leg).map(([k, v]) => (
                    <tr key={k} className="border-t">
                      <td className="py-2 px-4 text-gray-600">{toLabel(k)}</td>
                      <td className="py-2 px-4 text-right text-gray-800">{displayValue(k, v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    );
  } else if (type === "passengers") {
    title = "Booking Passenger Details";
    const pax = Array.isArray(p.passengers) ? p.passengers : [];
    body = (
      <div className="space-y-3">
        {pax.length === 0 ? (
          <div className="text-sm text-gray-600">No passengers stored.</div>
        ) : (
          pax.map((person, idx) => (
            <div key={idx} className="rounded-xl border overflow-hidden">
              <div className="bg-sky-100 px-4 py-2 text-sm font-bold text-gray-800">
                {person.title} {person.first_name} {person.last_name} {person.is_lead ? "(Lead)" : ""}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {[
                    ["Type", person.pax_type === 1 ? "Adult" : person.pax_type === 2 ? "Child" : person.pax_type === 3 ? "Infant" : person.pax_type],
                    ["DOB", person.dob],
                    ["Gender", person.gender === 1 ? "Male" : person.gender === 2 ? "Female" : person.gender],
                    ["Email", person.email],
                    ["Contact No", person.contact_no],
                    ["Nationality", person.nationality],
                    ["Document Name", person.document_name],
                    ["Document Number", person.document_number],
                    ["Passport No", person.passport_no],
                    ["Passport Expiry", person.passport_expiry],
                    ["Passport Issue Date", person.passport_issue_date],
                    ["Passport Issue Country", person.passport_issue_countrycode],
                  ].map(([label, value]) => (
                    <tr key={label} className="border-t">
                      <td className="py-2 px-4 text-gray-600">{label}</td>
                      <td className="py-2 px-4 text-right text-gray-800">{value || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PassengerSsrTable person={person} />
            </div>
          ))
        )}
      </div>
    );
  } else if (type === "fare_info") {
    title = "Booking Fare Info Summary";
    const rows = Array.isArray(p.fare_details?.fare_info) ? p.fare_details.fare_info : [];
    body = (
      <div className="rounded-xl overflow-hidden border w-full max-w-md">
        <div className="bg-sky-100 px-4 py-2.5">
          <span className="font-bold text-gray-800">Fare Summary</span>
        </div>
        {rows.length > 0 ? (
          <table className="w-full text-sm">
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="py-2 px-4 text-gray-700">{row.label}</td>
                  <td className="py-2 px-4 text-right tabular-nums text-gray-700">{formatINR(row.value)}</td>
                </tr>
              ))}
              <tr className="bg-gray-100">
                <td className="py-2.5 px-4 font-bold text-gray-800">Total</td>
                <td className="py-2.5 px-4 text-right font-bold text-gray-800 tabular-nums">
                  {formatINR(p.fare_details?.total ?? p.fare_details?.payable ?? p.grand_total)}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div className="p-3 text-sm text-gray-600">No fare info</div>
        )}
      </div>
    );
  } else if (type === "fare_detail") {
    title = "Search Fare Detail";
    const fd = p.fare_details || {};
    const entries = Object.entries(fd).filter(([k]) => k !== "fare_info");
    body = (
      <table className="w-full text-sm">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-t">
              <td className="py-2 px-4 text-gray-600">{toLabel(k)}</td>
              <td className="py-2 px-4 text-right text-gray-800 break-all">{displayValue(k, v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold hover:bg-gray-100">
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto flex-1 min-h-0">{body}</div>
      </div>
    </div>
  );
}

export default function BookingAttemptDetailPage() {
  const { traceId } = useParams();
  const navigate = useNavigate();

  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [activeTab, setActiveTab] = useState("onward");
  // Multi city only: id of the selected "Trip N" attempt (null = first trip).
  const [activeTripAttemptId, setActiveTripAttemptId] = useState(null);
  const [pnrByAttemptId, setPnrByAttemptId] = useState({});
  const [previewByAttemptId, setPreviewByAttemptId] = useState({});
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [payloadModal, setPayloadModal] = useState(null);
  const [detailsTab, setDetailsTab] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr("");
    api
      .get(`/admin/booking-attempts/by-trace/${traceId}`)
      .then((res) => setAttempts(res.data?.data || []))
      .catch((e) => {
        const msg = e?.response?.data?.message || e.message || "Failed to load booking attempt";
        setErr(msg);
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, [traceId]);

  // trip_indicator_id isn't always populated on stored attempts (seen null
  // on both legs of a real round trip) — fall back to array order (index 0 =
  // onward, index 1 = return) so a round trip's second leg is always
  // reachable in the UI even when that column is missing.
  const onwardAttempt = attempts.find((a) => a.trip_indicator_id === 1) || attempts[0];
  const returnAttempt = attempts.find((a) => a.trip_indicator_id === 2) || (attempts.length > 1 ? attempts.find((a) => a !== onwardAttempt) : undefined);
  const hasTabs = attempts.length > 1;
  const tripTabs = hasTabs && attempts.some(isMultiCityAttempt) ? multiCityAttemptTrips(attempts) : null;
  const current = tripTabs
    ? (tripTabs.find((t) => t.attempt.id === activeTripAttemptId) || tripTabs[0]).attempt
    : hasTabs ? (activeTab === "return" ? returnAttempt : onwardAttempt) : attempts[0];

  const setPnr = (attemptId, value) => setPnrByAttemptId((prev) => ({ ...prev, [attemptId]: value }));

  const handlePreview = async (attempt) => {
    const pnr = (pnrByAttemptId[attempt.id] || "").trim();
    if (!pnr) {
      toast.error("Enter a PNR first.");
      return;
    }
    setPreviewLoadingId(attempt.id);
    try {
      const res = await api.post(`/admin/booking-attempts/${traceId}/preview`, { attempt_id: attempt.id, pnr });
      setPreviewByAttemptId((prev) => ({ ...prev, [attempt.id]: res.data?.data || null }));
      toast.success("TBO confirmed this PNR — review the details below, then Create.");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Could not verify this PNR with TBO";
      toast.error(msg);
      setPreviewByAttemptId((prev) => ({ ...prev, [attempt.id]: null }));
    } finally {
      setPreviewLoadingId(null);
    }
  };

  // A leg with a pending bookings row is always actionable (that row gets
  // updated); a leg whose booking is already BOOKED never is.
  const isActionable = (a) => Boolean(a?.is_pending_booking) || (a?.status !== "resolved" && !a?.is_already_booked);
  const isPendingTrace = attempts.some((a) => a.is_pending_booking);

  const legsToCreate = useMemo(
    () => attempts.filter(isActionable).map((a) => ({ attempt_id: a.id, pnr: (pnrByAttemptId[a.id] || "").trim() })),
    [attempts, pnrByAttemptId]
  );
  const canCreate = legsToCreate.length > 0 && legsToCreate.every((l) => l.pnr);

  const handleCreate = async () => {
    if (!canCreate) {
      toast.error("Enter a PNR for every unresolved leg before creating.");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post(`/admin/booking-attempts/${traceId}/create-booking`, { legs: legsToCreate });
      const results = res.data?.data || [];
      results.forEach((r) => {
        if (r.ok && r.updated_existing) toast.success(`Pending booking updated (PNR ${r.pnr}), booking_id ${r.booking_id}`);
        else if (r.ok) toast.success(`Booking created (PNR ${r.pnr}) — booking_id ${r.booking_id}`);
        else toast.error(`Leg ${r.attempt_id}: ${r.message || "failed"}`);
      });
      if (results.some((r) => r.ok)) {
        navigate("/admin/booking-attempts");
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Booking creation failed";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Create Booking From Attempt</h1>
        <button type="button" onClick={() => navigate("/admin/booking-attempts")} className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50">
          Back to List
        </button>
      </div>

      {err ? (
        <div className="text-sm text-red-600">{err}</div>
      ) : loading ? (
        <div className="text-sm text-gray-600">Loading...</div>
      ) : !current ? (
        <div className="text-sm text-gray-600">Booking attempt not found.</div>
      ) : (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex items-center justify-between flex-wrap gap-2">
            <span>Trace ID: {traceId}</span>
            {tripTabs ? (
              <div className="flex flex-wrap rounded-lg border border-gray-300 overflow-hidden">
                {tripTabs.map(({ attempt, label }) => (
                  <button
                    key={attempt.id}
                    type="button"
                    onClick={() => setActiveTripAttemptId(attempt.id)}
                    className={`px-4 py-2 text-sm font-semibold ${current.id === attempt.id ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : hasTabs && (
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setActiveTab("onward")}
                  className={`px-4 py-2 text-sm font-semibold ${activeTab === "onward" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  Onward
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("return")}
                  disabled={!returnAttempt}
                  className={`px-4 py-2 text-sm font-semibold disabled:opacity-40 ${activeTab === "return" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  Return
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            {current.is_pending_booking ? (
              <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                This leg already has a pending booking (booking_id {current.linked_booking_id}). Verifying the PNR will update that booking instead of creating a new one.
              </div>
            ) : current.is_already_booked ? (
              <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                This leg is already booked (booking_id {current.linked_booking_id}).
              </div>
            ) : current.status === "resolved" ? (
              <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                This leg has already been resolved into a booking.
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {payloadFields(current).map((f) => (
                <div key={f.label} className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-600">{f.label}</label>
                  <div className="mt-1 p-3 rounded-xl bg-gray-50 text-sm border min-h-[44px]">
                    {formatVal(f.value)}
                    {f.hint ? <span className="ml-2 text-xs text-gray-500">({f.hint})</span> : null}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-gray-600 block mb-2">Booking Details</label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                  disabled={!current.booking_payload?.segments}
                  onClick={() => setDetailsTab("search")}
                >
                  User Booking Data
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                  disabled={!current.booking_payload?.passengers?.length}
                  onClick={() => setDetailsTab("passengers")}
                >
                  Booking Passenger Details
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                  disabled={!current.booking_payload?.fare_details?.fare_info}
                  onClick={() => setDetailsTab("fare_info")}
                >
                  Booking Fare Info Summary
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                  disabled={!current.booking_payload?.fare_details}
                  onClick={() => setDetailsTab("fare_detail")}
                >
                  Search Fare Detail
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                  onClick={() => setPayloadModal({ title: "Full booking_payload", raw: current.booking_payload })}
                >
                  View Raw Payload
                </button>
              </div>
            </div>

            {isActionable(current) && (
              <div className="mt-6 p-4 border rounded-xl bg-gray-50 border-amber-200">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  Enter PNR {isMultiCityAttempt(current) && attemptTripNumber(current) ? `(Trip ${attemptTripNumber(current)})` : current.trip_indicator_id === 2 ? "(Return leg)" : current.trip_indicator_id === 1 ? "(Onward leg)" : ""}
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  The airline PNR for this leg (looked up against TBO to pull confirmed passengers/segments/fare before creating the booking).
                </p>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="w-full sm:w-64">
                    <input
                      type="text"
                      className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                      placeholder="PNR"
                      value={pnrByAttemptId[current.id] || ""}
                      onChange={(e) => setPnr(current.id, e.target.value.toUpperCase())}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={previewLoadingId === current.id}
                    onClick={() => handlePreview(current)}
                    className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-white disabled:opacity-60"
                  >
                    {previewLoadingId === current.id ? "Checking with TBO..." : "Verify with TBO"}
                  </button>
                </div>

                {previewByAttemptId[current.id] && (
                  <div className="mt-4 rounded-xl border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                    <div className="font-semibold mb-1">TBO confirmed this PNR:</div>
                    <div>Origin/Destination: {previewByAttemptId[current.id].origin} &rarr; {previewByAttemptId[current.id].destination}</div>
                    <div>Airline: {previewByAttemptId[current.id].airline_code || "-"}</div>
                    <div>
                      Passengers:{" "}
                      {(previewByAttemptId[current.id].passengers || [])
                        .map((p) => `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim())
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                disabled={!canCreate || creating}
                onClick={handleCreate}
                className="rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {creating
                  ? isPendingTrace ? "Updating..." : "Creating..."
                  : isPendingTrace
                  ? hasTabs ? "Update Booking(s)" : "Update Booking"
                  : hasTabs ? "Create Booking(s)" : "Create Booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {payloadModal && <JsonViewerModal title={payloadModal.title} raw={payloadModal.raw} onClose={() => setPayloadModal(null)} />}
      {detailsTab && <BookingDetailsTabsModal type={detailsTab} payload={current?.booking_payload} onClose={() => setDetailsTab(null)} />}
    </div>
  );
}
